import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { find, reject, sortBy, uniqBy } from "lodash";
import toast from "react-hot-toast";
import { GET_ALL_PORTFOLIO_LIST, GET_PORTFOLIO_BY_ID } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import { refetchQuery } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";

interface PortfolioState {
  loading: boolean;
  error: string | null;
  extraThings: {
    brokerList: { name: string; value: string }[];
  };
}

const initialState: PortfolioState = {
  loading: false,
  error: null,
  extraThings: {
    brokerList: [],
  },
};

export const createPortfolio = createAsyncThunk(
  "portfolio/create",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(GET_ALL_PORTFOLIO_LIST, data);
      refetchQuery("GET_ALL_COMPLIANCE_BY_CLIENT_ID");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong",
      );
    }
  },
);

export const updatePortfolio = createAsyncThunk(
  "portfolio/update",
  async (data: any, { rejectWithValue }) => {
    try {
      const { id, portfolioUpdateData } = data;
      const response = await axiosInstance.patch(GET_PORTFOLIO_BY_ID(id), {
        ...portfolioUpdateData,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong",
      );
    }
  },
);

export const deletePortfolio = createAsyncThunk(
  "portfolio/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(GET_PORTFOLIO_BY_ID(id));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Something went wrong",
      );
    }
  },
);

const portfolioSlice = createSlice({
  name: "portfolio",
  initialState,
  reducers: {
    setBrokerList: (state, action) => {
      const rawList = Array.isArray(action.payload) ? action.payload : [];

      const validList = rawList.filter(
        (item): item is { name: string; value: string } =>
          typeof item === "object" &&
          item !== null &&
          typeof item.name === "string" &&
          typeof item.value === "string",
      );

      const uniqueList = uniqBy(validList, "value");

      const otherEntry = find(
        uniqueList,
        (item) => item.value?.toLowerCase() === "other",
      );
      const rest = sortBy(
        reject(uniqueList, (item) => item.name?.toLowerCase() === "other"),
        "name",
      );

      state.extraThings.brokerList = otherEntry ? [...rest, otherEntry] : rest;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createPortfolio.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPortfolio.fulfilled, (state, action) => {
        state.loading = false;
        toast.success(
          action.payload?.message || "Portfolio created successfully",
        );
        refetchQuery("GET_ALL_PORTFOLIOS");
        refetchQuery("GET_UPLOAD_PORTFOLIO_DROPDOWN");
        refetchQuery(QueryKeys.PORTFOLIOS_SHORT_DATA);
      })
      .addCase(createPortfolio.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error((action.payload as string) || "Failed to create portfolio");
        // const errorMessage =
        //   (action.payload as string) || "Failed to create portfolio";

        // // Check if it's the advisor limit error
        // if (
        //   errorMessage ===
        //   "Portfolio creation failed: the advisor has reached the maximum allowed limit."
        // ) {
        //   showAdvisorLimitToast();
        // } else {
        //   toast.error(errorMessage);
        // }
      })
      .addCase(updatePortfolio.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePortfolio.fulfilled, (state, action) => {
        state.loading = false;
        toast.success(
          action.payload?.message || "Action Performed Successfully.",
        );
        refetchQuery("GET_PORTFOLIO_DETAIL_BY_ID");
        refetchQuery("PORTFOLIO_UPDATE_DATA_BY_ID");
        refetchQuery("GET_PORTFOLIO_DETAILS");
      })
      .addCase(updatePortfolio.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(
          (action.payload as string) ||
            "Something went wrong, please try again.",
        );
      })
      .addCase(deletePortfolio.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePortfolio.fulfilled, (state, action) => {
        state.loading = false;
        toast.success(
          action.payload?.message || "Action Performed Successfully.",
        );
      })
      .addCase(deletePortfolio.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(
          (action.payload as string) ||
            "Something went wrong, please try again.",
        );
      });
  },
});

export const { setBrokerList } = portfolioSlice.actions;

export default portfolioSlice.reducer;
